import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { FortnoxProcessedPayout } from '@/types';

const FortnoxProcessedPayouts: React.FC = () => {
  const [filters, setFilters] = useState({
    dateCreatedFrom: '',
    dateCreatedTo: '',
    status: '',
    paymentReference: '',
  });

  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState<{ objects: FortnoxProcessedPayout[], page: number, total: number } | null>(null);
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    perPage: 25,
  });
  
  const [sorting, setSorting] = useState({
    sortBy: 'id',
    sortOrder: 'desc' as 'asc' | 'desc',
  });

  const queryClient = useQueryClient();

  // Helper function to convert camelCase to snake_case for API
  const convertToApiParams = (filters: Record<string, string>) => {
    return Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== '') {
        const apiKey = key === 'dateCreatedFrom' ? 'date_created_from' :
                      key === 'dateCreatedTo' ? 'date_created_to' :
                      key === 'paymentReference' ? 'payment_reference' :
                      key;
        acc[apiKey] = value;
      }
      return acc;
    }, {} as Record<string, string>);
  };


  // Get first site ID (matching original behavior)
  const { data: sites = [] } = useQuery('sites', apiService.getSites);
  const siteId = sites.length > 0 ? sites[0].id : null;

  // Fetch Fortnox processed payouts
  const { data: payoutsData, isLoading } = useQuery(
    ['fortnoxProcessedPayouts', siteId, pagination.currentPage, sorting.sortBy, sorting.sortOrder],
    () => apiService.getFortnoxProcessedPayouts(siteId!, {
      order_by: sorting.sortBy,
      order_type: sorting.sortOrder,
      page: pagination.currentPage,
    }),
    {
      enabled: !!siteId,
      onSuccess: (data) => {
        setPagination(prev => ({
          ...prev,
          totalPages: Math.ceil(data.total / prev.perPage),
        }));
      },
    }
  );


  // Search mutation
  const searchMutation = useMutation(
    (searchParams: Record<string, any>) => apiService.searchFortnoxProcessedPayouts(siteId!, searchParams),
    {
      onSuccess: (data) => {
        setPagination(prev => ({
          ...prev,
          totalPages: Math.ceil(data.total / prev.perPage),
        }));
        // Store search results in state
        setSearchResults(data);
      },
      onError: (error) => {
        console.error('Search failed:', error);
        alert('Search failed');
      },
    }
  );

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'error', label: 'Error' },
    { value: 'completed', label: 'Completed' },
  ];

  // Initialize filters from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setFilters({
      dateCreatedFrom: urlParams.get('date_created_from') || '',
      dateCreatedTo: urlParams.get('date_created_to') || '',
      status: urlParams.get('status') || '',
      paymentReference: urlParams.get('payment_reference') || '',
    });
    
    const page = urlParams.get('page');
    if (page) {
      setPagination(prev => ({ ...prev, currentPage: parseInt(page) }));
    }
    
    const orderBy = urlParams.get('order_by');
    const orderType = urlParams.get('order_type');
    if (orderBy && orderType) {
      setSorting({
        sortBy: orderBy,
        sortOrder: orderType as 'asc' | 'desc',
      });
    }
  }, []);

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    const searchParams = convertToApiParams(filters);

    // Reset to page 1 when performing search
    setPagination(prev => ({ ...prev, currentPage: 1 }));

    // Add pagination and sorting to search params (always start from page 1)
    const fullSearchParams = {
      ...searchParams,
      order_by: sorting.sortBy,
      order_type: sorting.sortOrder,
      page: 1,
    };

    setIsSearchActive(true);
    searchMutation.mutate(fullSearchParams);
  };

  const handleClearSearch = () => {
    setFilters({
      dateCreatedFrom: '',
      dateCreatedTo: '',
      status: '',
      paymentReference: '',
    });
    setIsSearchActive(false);
    setSearchResults(null);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    queryClient.invalidateQueries(['fortnoxProcessedPayouts']);
  };

  const handleSort = (field: string) => {
    const newOrder = sorting.sortBy === field && sorting.sortOrder === 'desc' ? 'asc' : 'desc';
    setSorting({
      sortBy: field,
      sortOrder: newOrder,
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));

    // If search is active, re-run search with new sorting
    if (isSearchActive) {
      const searchParams = convertToApiParams(filters);
      const fullSearchParams = {
        ...searchParams,
        order_by: field,
        order_type: newOrder,
        page: 1,
      };
      searchMutation.mutate(fullSearchParams);
    }
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page,
    }));

    // If search is active, re-run search with new page
    if (isSearchActive) {
      const searchParams = convertToApiParams(filters);
      const fullSearchParams = {
        ...searchParams,
        order_by: sorting.sortBy,
        order_type: sorting.sortOrder,
        page: page,
      };
      searchMutation.mutate(fullSearchParams);
    }
  };

  const getSortIcon = (field: string) => {
    if (sorting.sortBy !== field) {
      return <FontAwesomeIcon icon={faSort} className="w-3 h-3 text-gray-400" />;
    }
    return sorting.sortOrder === 'asc' ? 
      <FontAwesomeIcon icon={faSortUp} className="w-3 h-3 text-orange-500" /> :
      <FontAwesomeIcon icon={faSortDown} className="w-3 h-3 text-orange-500" />;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="badge badge-success">Completed</span>;
      case 'error':
        return <span className="badge badge-danger">Error</span>;
      default:
        return <span className="badge badge-secondary">{status}</span>;
    }
  };

  const processedPayouts = isSearchActive && searchResults ? (searchResults.objects || []) : (payoutsData?.objects || []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">Fortnox Processed Payouts</h1>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="form-section">
          <div className="form-row">
            <div className="form-column">
              <div>
                <label className="form-label">From Date:</label>
                <input
                  type="date"
                  value={filters.dateCreatedFrom}
                  onChange={(e) => handleFilterChange('dateCreatedFrom', e.target.value)}
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">To Date:</label>
                <input
                  type="date"
                  value={filters.dateCreatedTo}
                  onChange={(e) => handleFilterChange('dateCreatedTo', e.target.value)}
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">Status:</label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="form-input"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-column">
              <div>
                <label className="form-label">Payment Reference:</label>
                <input
                  type="text"
                  value={filters.paymentReference}
                  onChange={(e) => handleFilterChange('paymentReference', e.target.value)}
                  className="form-input"
                  placeholder="Enter payment reference"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 space-x-3">
            {isSearchActive && (
              <button
                onClick={handleClearSearch}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
              >
                Clear Search
              </button>
            )}
            <button
              onClick={handleSearch}
              disabled={searchMutation.isLoading}
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
            >
              {searchMutation.isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSearch} className="w-4 h-4" />
                  <span>Search</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Payouts Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center space-x-1">
                    <span>ID</span>
                    {getSortIcon('id')}
                  </div>
                </th>
                <th>Site ID</th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('date_created')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Date Created</span>
                    {getSortIcon('date_created')}
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('payment_reference')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Payment Reference</span>
                    {getSortIcon('payment_reference')}
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('amount')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Amount</span>
                    {getSortIcon('amount')}
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {getSortIcon('status')}
                  </div>
                </th>
                <th 
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleSort('status_message')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status Message</span>
                    {getSortIcon('status_message')}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {processedPayouts.map((payout: FortnoxProcessedPayout) => (
                <tr key={payout.id}>
                  <td className="font-mono text-sm">{payout.id}</td>
                  <td>{payout.site_id}</td>
                  <td className="text-sm text-gray-600">
                    {formatDate(payout.date_created)}
                  </td>
                  <td className="font-mono text-sm">{payout.payment_reference}</td>
                  <td className="font-mono text-sm font-semibold text-green-600">
                    {formatAmount(payout.amount)}
                  </td>
                  <td>{getStatusBadge(payout.status)}</td>
                  <td className="max-w-xs truncate text-sm text-gray-600">
                    {payout.status_message || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Page {pagination.currentPage} of {pagination.totalPages}
            {isSearchActive && searchResults && (
              <span className="ml-2 text-orange-600">
                ({searchResults.total} total results)
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage <= 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 rounded ${
                      page === pagination.currentPage
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage >= pagination.totalPages}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FortnoxProcessedPayouts;


